import { useTranslation } from 'react-i18next';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { useInstantPayCashFlowQuarterly, type CashFlowChartView } from './useInstantPayCashFlow';

const GREEN_300 = '#A1CFB6';
const GREEN_700 = '#058154';
const TEAL_900 = '#00545F';

interface ChartDataPoint {
  quarter: string;
  cashBenefit: number;
  dsoPercent: number;
}

interface DotProps {
  cx?: number;
  cy?: number;
}

interface TickProps {
  x?: number;
  y?: number;
  payload?: { value: number };
  textAnchor?: 'inherit' | 'start' | 'end' | 'middle';
  dx?: number;
  formatter?: (value: number) => string;
}

const CenteredTick = ({ x = 0, y = 0, payload, textAnchor = 'end', dx = 0, formatter }: TickProps) => (
  <text x={x + dx} y={y} textAnchor={textAnchor} dominantBaseline="middle" fontSize={14} fill="rgba(33,31,31,0.64)">
    {formatter ? formatter(payload?.value ?? 0) : payload?.value}
  </text>
);

interface QuarterTickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
}

// Quarter axis values are pre-composed as "label\nrange" — recharts ticks only receive the raw
// axis value for the row, so both lines are packed into one string and split here for rendering.
const QuarterTick = ({ x = 0, y = 0, payload }: QuarterTickProps) => {
  const [quarterLabel, quarterRangeLabel] = (payload?.value ?? '').split('\n');

  return (
    <g transform={`translate(${x}, ${y})`}>
      <text textAnchor="middle" dy={16} fontSize={14} fontWeight={600} className="fill-text-secondary">
        {quarterLabel}
      </text>
      <text textAnchor="middle" dy={34} fontSize={12} className="fill-text-secondary">
        {quarterRangeLabel}
      </text>
    </g>
  );
};

interface AxisLabelProps {
  viewBox?: { x: number; y: number; width: number; height: number };
  value?: string;
  axis?: 'left' | 'right';
  maxTickWidth?: number;
}

// Indicator sits just before the start of the label text (left-of-text, pre-rotation)
// so it lands ahead of the text once the whole group is rotated to run along the axis.
// Text width is measured via canvas rather than estimated from character count, so the
// gap to the indicator stays visually consistent across labels of different lengths.
const AXIS_LABEL_FONT = '14px sans-serif';
const INDICATOR_GAP = 14;
// Gap between the tick numbers' outer edge and the axis label, kept the same on both
// sides so the label reads at an equal distance from its tick text regardless of how
// wide the currency vs. percent tick strings happen to be.
const TICK_TO_LABEL_GAP = 24;
// Right-axis ticks are drawn with a dx of 8 (see the YAxis tick render below), so their
// text starts 8px past the axis line — accounted for here to keep both gaps equal.
const RIGHT_TICK_DX = 8;
// Horizontal footprint of the rotated label text (its line thickness, not its length).
const AXIS_LABEL_THICKNESS = 20;

let measureCanvas: HTMLCanvasElement | undefined;

const measureTextWidth = (text: string, font: string): number => {
  try {
    measureCanvas ??= document.createElement('canvas');
    const context = measureCanvas.getContext('2d');
    if (!context) return 0;
    context.font = font;
    return context.measureText(text).width;
  } catch {
    // jsdom (used in tests) has no canvas 2D context implementation.
    return 0;
  }
};

const BarIndicator = ({ cx, cy }: { cx: number; cy: number }) => (
  <rect x={cx - 6} y={cy - 6} width={12} height={12} rx={2} fill={GREEN_300} stroke={GREEN_700} strokeWidth={1} />
);

const LineIndicator = ({ cx, cy }: { cx: number; cy: number }) => (
  <>
    <line x1={cx - 10} y1={cy} x2={cx + 10} y2={cy} stroke={TEAL_900} strokeWidth={2} />
    <circle cx={cx} cy={cy} r={5} fill={TEAL_900} />
  </>
);

const CenteredAxisLabel = ({
  viewBox = { x: 0, y: 0, width: 0, height: 0 },
  value = '',
  axis = 'left',
  maxTickWidth = 0,
}: AxisLabelProps) => {
  // The axis line sits at the inner edge of this label's viewBox: for the left axis
  // that's the right edge (viewBox.x + width), for the right axis it's the left edge.
  const axisLineX = axis === 'left' ? viewBox.x + viewBox.width : viewBox.x;
  const tickTextEdge = axis === 'left' ? maxTickWidth : RIGHT_TICK_DX + maxTickWidth;
  const cx =
    axis === 'left' ? axisLineX - tickTextEdge - TICK_TO_LABEL_GAP : axisLineX + tickTextEdge + TICK_TO_LABEL_GAP;
  const cy = viewBox.y + viewBox.height / 2;
  const rotate = axis === 'left' ? -90 : 90;
  const halfTextWidth = measureTextWidth(value, AXIS_LABEL_FONT) / 2;
  const indicatorX = cx - halfTextWidth - INDICATOR_GAP;

  return (
    <g transform={`rotate(${rotate}, ${cx}, ${cy})`}>
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fontSize={14} fill="rgba(33,31,31,0.64)">
        {value}
      </text>
      {axis === 'left' ? <BarIndicator cx={indicatorX} cy={cy} /> : <LineIndicator cx={indicatorX} cy={cy} />}
    </g>
  );
};

interface CustomTooltipProps extends TooltipProps<number, string> {
  benefitName: string;
  benefitLabel: string;
  dsoName: string;
  dsoLabel: string;
  formatCurrency: (value: number) => string;
  formatPercent: (value: number) => string;
}

const CustomTooltip = ({
  active,
  payload,
  label,
  benefitName,
  benefitLabel,
  dsoName,
  dsoLabel,
  formatCurrency,
  formatPercent,
}: CustomTooltipProps) => {
  if (!active || !payload || payload.length === 0) return null;

  const cashEntry = payload.find((p) => p.name === benefitName);
  const dsoEntry = payload.find((p) => p.name === dsoName);

  return (
    <div className="rounded-lg border border-gray-50 bg-white p-2 text-sm shadow-md" style={{ minWidth: 360 }}>
      <p className="mb-1 font-bold text-text-primary">{label}</p>
      {cashEntry != null && (
        <div className="mb-1 flex items-center gap-1 text-text-primary">
          <span className="inline-block size-3 shrink-0 rounded-sm border border-green-700 bg-green-300" />
          <span>
            {benefitLabel}: <strong>{formatCurrency(cashEntry.value ?? 0)}</strong>
          </span>
        </div>
      )}
      {dsoEntry != null && (
        <div className="flex items-center gap-1 text-text-primary">
          <span className="inline-block size-3 shrink-0 rounded-full bg-teal-900" />
          <span>
            {dsoLabel}: <strong>{formatPercent(dsoEntry.value ?? 0)}</strong>
          </span>
        </div>
      )}
    </div>
  );
};

// Calculates a clean domain max and 4 evenly-spaced ticks (including 0) from raw data values.
// The domain max is rounded up to the nearest "nice" step so ticks land on readable numbers.
export const calcAxisScale = (values: number[]): { domain: [number, number]; ticks: number[] } => {
  const max = Math.max(0, ...values);
  if (max === 0) {
    return { domain: [0, 0], ticks: [0, 0, 0, 0, 0] };
  }

  const rawStep = max / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const niceStep = Math.ceil(rawStep / magnitude) * magnitude;
  const domainMax = niceStep * 4;
  return {
    domain: [0, domainMax],
    ticks: [0, niceStep, niceStep * 2, niceStep * 3, domainMax],
  };
};

export const calcPercentScale = (values: number[]): { domain: [number, number]; ticks: number[] } => {
  const max = Math.max(0, ...values);
  const step = 1 / 3;
  const domainMax = Math.ceil(max / step) * step;
  const tickStep = domainMax / 4;
  return {
    domain: [0, domainMax],
    ticks: [0, tickStep, tickStep * 2, tickStep * 3, domainMax],
  };
};

interface InstantPayCashFlowChartProps {
  view: CashFlowChartView;
}

const InstantPayCashFlowChart = ({ view }: InstantPayCashFlowChartProps) => {
  const { t } = useTranslation();
  const { asCurrency, asCurrencyCompact, asDateString, asPercent } = useLocaleFormat();
  const quarterlyData = useInstantPayCashFlowQuarterly()[view];
  const data: ChartDataPoint[] = quarterlyData.map((point) => {
    const [year, quarterNumber] = point.yearQuarter.split('-Q');
    const firstMonth = point.months[0];
    const lastMonth = point.months[point.months.length - 1];
    const startMonthLabel = asDateString(`${firstMonth}-01`, { month: 'short', year: undefined, day: undefined });
    const quarterLabel = t('instantPay.cashFlowChart.quarterAxisLabel', { number: quarterNumber });
    const quarterRangeLabel =
      point.months.length > 1
        ? t('instantPay.cashFlowChart.quarterAxisRange', {
            startMonth: startMonthLabel,
            endMonth: asDateString(`${lastMonth}-01`, { month: 'short', year: undefined, day: undefined }),
            year,
          })
        : t('instantPay.cashFlowChart.quarterAxisSingleMonth', { month: startMonthLabel, year });

    return {
      quarter: `${quarterLabel}\n${quarterRangeLabel}`,
      cashBenefit: point.cashBenefit,
      dsoPercent: point.dsoPercent,
    };
  });
  const cashScale = calcAxisScale(data.map((d) => d.cashBenefit));
  const percentScale = calcPercentScale(data.map((d) => d.dsoPercent));

  const formatCashAxis = (value: number): string => asCurrencyCompact(value, 'USD');
  const formatPercentAxis = (value: number): string => asPercent({ value });
  const formatCurrencyFull = (value: number): string => asCurrency(value, 'USD');
  const formatTooltipPercent = (value: number): string => asPercent({ value, decimals: 1 });

  const maxCashTickWidth = Math.max(
    ...cashScale.ticks.map((tick) => measureTextWidth(formatCashAxis(tick), AXIS_LABEL_FONT))
  );
  const maxPercentTickWidth = Math.max(
    ...percentScale.ticks.map((tick) => measureTextWidth(formatPercentAxis(tick), AXIS_LABEL_FONT))
  );
  // Room for the tick text, the equal gap to the label, and the rotated label's own thickness.
  const leftAxisWidth = maxCashTickWidth + TICK_TO_LABEL_GAP + AXIS_LABEL_THICKNESS;
  const rightAxisWidth = RIGHT_TICK_DX + maxPercentTickWidth + TICK_TO_LABEL_GAP + AXIS_LABEL_THICKNESS;

  const benefitName = t('instantPay.cashFlowChart.instantPayBenefitLegend');
  const percentName = t('instantPay.cashFlowChart.percentImprovementLegend');

  return (
    <ResponsiveContainer width="100%" height={388}>
      <ComposedChart data={data} margin={{ top: 10, right: 4 }}>
        <CartesianGrid vertical={false} stroke="rgba(33,31,31,0.3)" strokeWidth={0.5} />
        <XAxis dataKey="quarter" tickLine={false} axisLine={false} height={50} tick={<QuarterTick />} />
        <YAxis
          yAxisId="left"
          orientation="left"
          domain={cashScale.domain}
          ticks={cashScale.ticks}
          tickLine={false}
          axisLine={false}
          tick={(props: TickProps) => <CenteredTick {...props} formatter={formatCashAxis} />}
          label={
            <CenteredAxisLabel
              value={t('instantPay.cashFlowChart.cashBenefitAxisLabel')}
              axis="left"
              maxTickWidth={maxCashTickWidth}
            />
          }
          width={leftAxisWidth}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          domain={percentScale.domain}
          ticks={percentScale.ticks}
          tickLine={false}
          axisLine={false}
          tick={(props: TickProps) => (
            <CenteredTick {...props} textAnchor="start" dx={8} formatter={formatPercentAxis} />
          )}
          label={
            <CenteredAxisLabel
              value={t('instantPay.cashFlowChart.percentImprovementAxisLabel')}
              axis="right"
              maxTickWidth={maxPercentTickWidth}
            />
          }
          width={rightAxisWidth}
        />
        <Tooltip
          content={
            <CustomTooltip
              benefitName={benefitName}
              benefitLabel={t('instantPay.cashFlowChart.tooltipInstantPayBenefit')}
              dsoName={percentName}
              dsoLabel={t('instantPay.cashFlowChart.tooltipDsoImpact')}
              formatCurrency={formatCurrencyFull}
              formatPercent={formatTooltipPercent}
            />
          }
        />
        <Bar
          yAxisId="left"
          dataKey="cashBenefit"
          name={benefitName}
          fill={GREEN_300}
          stroke={GREEN_700}
          strokeWidth={1}
          radius={[2, 2, 0, 0]}
          barSize={32}
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="dsoPercent"
          name={percentName}
          stroke={TEAL_900}
          strokeWidth={3}
          dot={(props: DotProps) => (
            <circle key={`${props.cx}-${props.cy}`} cx={props.cx} cy={props.cy} r={4} fill={TEAL_900} />
          )}
          activeDot={{ r: 5, fill: TEAL_900, stroke: TEAL_900 }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default InstantPayCashFlowChart;
